import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getActiveOrgId } from "@/api/session";
import { useAckMatch, MONITORS_KEYS, type MonitorMatch } from "./api";

/**
 * Mounted once at the AppShell. Subscribes to UPDATE/INSERT events on
 * monitor_matches for the current user. Confirmed matches fire a toast,
 * invalidate the inbox queries, and implicitly ack themselves.
 *
 * IMPORTANT: backend writes confirms as UPDATE (candidate → confirmed),
 * not INSERT. Both events are subscribed; handler filters by stage.
 */
export function useMatchesRealtime(userId: string | undefined): void {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // useMutation returns a new wrapper object on every render, so depending on
  // it directly would tear down and re-subscribe the websocket every render.
  // Stash mutate in a ref and keep the effect deps to the stable identities.
  const ackMutation = useAckMatch();
  const ackRef = useRef(ackMutation.mutate);
  ackRef.current = ackMutation.mutate;

  useEffect(() => {
    if (!userId) return;

    function handle(payload: { new: MonitorMatch & { org_id?: string } }) {
      const row = payload.new;
      if (row.stage !== "confirmed" || row.delivered_at !== null) return;
      // If the payload carries an org_id, drop rows that don't match the
      // current active org. The Supabase channel filter only scopes by
      // user_id; this guards against cross-org leakage once an org-switcher
      // UI exists (or a user belongs to multiple orgs).
      const activeOrgId = getActiveOrgId();
      if (row.org_id && activeOrgId && row.org_id !== activeOrgId) return;

      qc.invalidateQueries({ queryKey: ["monitors", "inbox"] });
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.unread });
      if (row.monitor_id) {
        qc.invalidateQueries({ queryKey: ["monitors", "matches"] });
      }

      toast(row.cluster_snapshot.title ?? "Monitor matched", {
        description: row.verifier_reason ?? "",
        duration: 8000,
        action: {
          label: "Open cluster",
          onClick: () => navigate(`/clusters/${row.cluster_id}`),
        },
      });

      ackRef.current(row.id);
    }

    const channel = supabase
      .channel(`monitor-matches-${userId}`)
      .on(
        "postgres_changes" as any,
        {
          event: "UPDATE",
          schema: "public",
          table: "monitor_matches",
          filter: `user_id=eq.${userId}`,
        },
        handle as any,
      )
      .on(
        "postgres_changes" as any,
        {
          event: "INSERT",
          schema: "public",
          table: "monitor_matches",
          filter: `user_id=eq.${userId}`,
        },
        handle as any,
      )
      .subscribe();

    const onFocus = () => {
      qc.invalidateQueries({ queryKey: MONITORS_KEYS.unread });
      qc.invalidateQueries({ queryKey: ["monitors", "inbox"] });
    };
    window.addEventListener("focus", onFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId, qc, navigate]);
}
